/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useRef, useState } from 'react'
import styled, { css } from 'styled-components'
import { gsap } from 'gsap'
import { down } from 'styled-breakpoints'

import { ReactComponent as AddCircle } from '../../assets/svg/add-circle-fill.svg'
import transitions from '../../styles/transitions'
import ModalPortal from './ModalPortal'
import { TABLET } from '../../lib/constant'

export default function Card({
	bgColor,
	title,
	icon,
	cardHeight,
	backTitle,
	backDescription,
}) {
	const cardRef = useRef(null)
	const portalRef = useRef(null)
	const cloneCard = useRef(null)
	const containerRef = useRef(null)
	const [open, setOpen] = useState(false)

	const onClick = useCallback(() => setOpen(true), [])
	const onClose = useCallback(() => setOpen(false), [])

	useEffect(() => {
		if (!portalRef.current || !cardRef.current || !containerRef.current) {
			return
		}
		if (open) {
			cloneCard.current = cardRef.current.cloneNode(true)
			cloneCard.current.addEventListener('click', (event) =>
				event.stopPropagation()
			)
			gsap.set(cloneCard.current, {
				position: 'absolute',
			})
			containerRef.current.appendChild(cloneCard.current)
			const cardOffset = cardRef.current.getBoundingClientRect()
			const { width, height } = cardOffset
			const tl = gsap.timeline()
			tl.set(cardRef.current, {
				opacity: 0,
			})
				.set(cloneCard.current, {
					left: 0,
					top: 0,
					right: 0,
					bottom: 0,
					width,
					height,
					zIndex: 9999999,
				})
				.to(cloneCard.current, {
					rotateY: 180,
					duration: 0.6,
				})
		} else {
			const cardOffset = cardRef.current.getBoundingClientRect()
			const { width, height } = cardOffset
			const tl = gsap
			tl.timeline()
				.to(cloneCard.current, {
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					width,
					height,
					y: '0%',
					x: '0%',
					rotateY: 0,
					duration: 0.6,
				})
				.set(cardRef.current, {
					opacity: 1,
				})
				.add(() => {
					if (!cloneCard.current || !portalRef.current) {
						return
					}
					containerRef.current.removeChild(cloneCard.current)
					cloneCard.current = null
				})
		}
	}, [open])

	const [hasDimmer, setHasDimmer] = useState(false)
	const timeout = useRef(null)

	useEffect(() => {
		if (open) {
			if (timeout.current) {
				clearTimeout(timeout.current)
			}
			setHasDimmer(true)
		} else {
			timeout.current = setTimeout(() => {
				setHasDimmer(false)
			}, 600)
		}
	}, [open])

	return (
		<>
			<Container onClick={onClick} ref={containerRef} cardHeight={cardHeight}>
				<Content ref={cardRef}>
					<Face bgColor={bgColor}>
						<IconWrapper>{icon}</IconWrapper>
						<h5>{title}</h5>
						<AddCircle fill="white" className="add-icon" />
					</Face>
					<Back bgColor={bgColor}>
						<h3>{backTitle}</h3>
						<p>{backDescription}</p>
					</Back>
				</Content>
			</Container>
			<ModalPortal>
				<PortalContainer open={hasDimmer} ref={portalRef}>
					{hasDimmer && <Dimmer open={open} onClick={onClose} />}
				</PortalContainer>
			</ModalPortal>
		</>
	)
}

const Container = styled.div`
	position: relative;
	overflow: visible;
	width: 480px;
	${({ cardHeight }) => cardHeight && `height: ${cardHeight}px;`};

	${down(TABLET)} {
		width: 100%;
		max-height: 400px;
	}

	&:not(:first-child) {
		margin-top: 20px;
	}
`

const Content = styled.div`
	position: relative;
	transform-style: preserve-3d;
	transform: perspective(2000px);
	width: 100%;
	height: 100%;
`

export const CardTemplate = styled.div`
	padding: 48px 32px;
	position: absolute;
	backface-visibility: hidden;
	border-radius: 32px;
	overflow: hidden;
	width: 100%;
	height: 100%;
	box-sizing: border-box;
	box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1),
		inset 0px 1px 2px rgba(255, 255, 255, 0.1);
	background: ${({ bgColor }) =>
		({
			green: 'linear-gradient(180deg, #b1f34c 0%, #6de214 100%)',
			yellow: 'linear-gradient(180deg, #FFDC60 0%, #FFB72B 100%);',
			red: 'linear-gradient(180deg, #B90C46 0%, #940132 100%);',
			orange: 'linear-gradient(180deg, #FF6D60 0%, #F64D3E 100%);',
			pink: 'linear-gradient(180deg, #D84576 0%, #C31B53 100%)',
		}[bgColor])};
`

const Face = styled(CardTemplate)`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	cursor: pointer;

	& > h5 {
		color: #fefefe;
		font-size: 48px;
		line-height: 56px;
		letter-spacing: -0.02em;
		font-weight: 700;
		white-space: pre-wrap;
		margin-top: 32px;
		margin-bottom: 48px;

		${down(TABLET)} {
			font-size: 32px;
			line-height: 40px;
		}
	}

	& > .add-icon {
		min-width: 36px;
		min-height: 36px;
		opacity: 0.2;
		fill: black;
	}
`

const Back = styled(CardTemplate)`
	transform: rotateY(180deg);
	cursor: default;
	color: white;
	overflow: auto;
	::-webkit-scrollbar {
		display: none;
	}

	> h3 {
		font-weight: bold;
		font-size: 48px;
		line-height: 56px;
		letter-spacing: -0.02em;
		margin-bottom: 32px;
	}

	> p {
		font-weight: 500;
		font-size: 40px;
		line-height: 48px;
		letter-spacing: -0.02em;
	}

	${down(TABLET)} {
		> h3 {
			font-size: 32px;
			line-height: 40px;
		}

		> p {
			font-size: 24px;
			line-height: 32px;
		}
	}
`

const Dimmer = styled.div`
	position: fixed;
	top: 0;
	left: 0;
	bottom: 0;
	right: 0;
	background: rgba(0, 0, 0, 0.7);
	backdrop-filter: blur(24px);
	${({ open }) => css`
		animation: ${open ? transitions.fadeIn : transitions.fadeOut} 0.6s forwards;
	`}
`

const PortalContainer = styled.div`
	position: fixed;
	z-index: 10000;
	top: 100px;
	left: 0px;
	bottom: 0px;
	right: 0px;
	${({ open }) =>
		open
			? css`
					bottom: 0px;
					right: 0px;
			  `
			: css`
					width: 0px;
					height: 0px;
					overflow: hidden;
			  `}
`

const IconWrapper = styled.div``
